import React, { useEffect, useMemo, useState } from 'react';
import {
  currentInstallState,
  installPromptSnoozed,
  requestNativeInstall,
  snoozeInstallPrompt,
  subscribeInstallState,
  type InstallState,
} from '../pwa/install';

const PwaInstallPrompt: React.FC = () => {
  const [state, setState] =
    useState<InstallState>(currentInstallState());

  const [visible, setVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    return subscribeInstallState(setState);
  }, []);

  useEffect(() => {
    if (state.installed || installPromptSnoozed()) {
      setVisible(false);
      return;
    }

    // Small delay avoids competing visually with initial authentication/loading.
    const timer = window.setTimeout(() => {
      if (
        state.nativePromptAvailable ||
        state.iosSafari ||
        state.macSafari
      ) {
        setVisible(true);
      }
    }, 1800);

    return () => window.clearTimeout(timer);
  }, [
    state.installed,
    state.nativePromptAvailable,
    state.iosSafari,
    state.macSafari,
  ]);

  const instructions = useMemo(() => {
    if (state.iosSafari) {
      return [
        'Tap the Share button in Safari.',
        'Choose “Add to Home Screen”.',
        'Enable “Open as Web App” if Safari offers it.',
        'Tap “Add”.',
      ];
    }

    if (state.macSafari) {
      return [
        'Open Safari’s File menu.',
        'Choose “Add to Dock”.',
        'Confirm CoConnect.',
      ];
    }

    return [];
  }, [state.iosSafari, state.macSafari]);

  if (!visible || state.installed) return null;

  const dismiss = () => {
    snoozeInstallPrompt();
    setVisible(false);
  };

  const install = async () => {
    if (state.nativePromptAvailable) {
      const result = await requestNativeInstall();

      if (result === 'accepted') {
        setVisible(false);
      }

      return;
    }

    setShowInstructions(true);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="coconnect-install-title"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 2147483000,
        maxWidth: 520,
        margin: '0 auto',
        borderRadius: 22,
        border: '1px solid rgba(148,163,184,.24)',
        background:
          'linear-gradient(145deg,rgba(15,23,42,.98),rgba(6,78,59,.98))',
        boxShadow: '0 24px 80px rgba(2,6,23,.42)',
        color: '#fff',
        padding: 20,
        backdropFilter: 'blur(18px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
        }}
      >
        <img
          src="/icons/icon-192.png"
          alt=""
          width={52}
          height={52}
          style={{
            borderRadius: 14,
            flex: '0 0 auto',
          }}
        />

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              opacity: .72,
              marginBottom: 4,
            }}
          >
            CoConnect Field Platform
          </div>

          <h2
            id="coconnect-install-title"
            style={{
              margin: 0,
              fontSize: 20,
              lineHeight: 1.2,
            }}
          >
            Install CoConnect
          </h2>

          <p
            style={{
              margin: '8px 0 0',
              fontSize: 14,
              lineHeight: 1.55,
              color: 'rgba(255,255,255,.82)',
            }}
          >
            Add CoConnect to this device for faster launch and
            resilient access to supported offline field workflows.
          </p>
        </div>
      </div>

      {!showInstructions ? (
        <>
          <div
            style={{
              display: 'grid',
              gap: 7,
              marginTop: 16,
              fontSize: 13,
              color: 'rgba(255,255,255,.85)',
            }}
          >
            <div>✓ Supported offline field workflows</div>
            <div>✓ Home-screen or desktop launch</div>
            <div>✓ Controlled application updates</div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
              marginTop: 18,
            }}
          >
            <button
              type="button"
              onClick={dismiss}
              style={{
                border: 0,
                borderRadius: 12,
                padding: '11px 15px',
                background: 'rgba(255,255,255,.10)',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Not now
            </button>

            <button
              type="button"
              onClick={install}
              style={{
                border: 0,
                borderRadius: 12,
                padding: '11px 16px',
                background: '#fff',
                color: '#064e3b',
                fontWeight: 850,
                cursor: 'pointer',
              }}
            >
              {state.nativePromptAvailable
                ? 'Install CoConnect'
                : 'Show install steps'}
            </button>
          </div>
        </>
      ) : (
        <>
          <ol
            style={{
              margin: '17px 0 0',
              paddingLeft: 22,
              lineHeight: 1.75,
              fontSize: 14,
            }}
          >
            {instructions.map(step => (
              <li key={step}>{step}</li>
            ))}
          </ol>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 16,
            }}
          >
            <button
              type="button"
              onClick={dismiss}
              style={{
                border: 0,
                borderRadius: 12,
                padding: '11px 16px',
                background: '#fff',
                color: '#064e3b',
                fontWeight: 850,
                cursor: 'pointer',
              }}
            >
              Got it
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PwaInstallPrompt;
